import { Redirect, Route, Switch, useLocation } from "wouter";
import { ThemeProvider } from "@mui/material/styles";
import { STATIC_REDIRECTS } from "../common/redirects";
import { ROUTE } from "../common/ROUTE";
import { useHashLocation } from "wouter/use-hash-location";
import { Layout } from "./components/Layout";
import { TabsTilesWrapper } from "./components/WhatsNew/Wrapper";
import About from "./pages/About/About";
import BlogPage from "./pages/Blog/BlogPage";
import BlogPostSsr from "./pages/Blog/BlogPost";
import CaseStudyPage from "./pages/CaseStudy/CaseStudyPage";
import CaseStudySlugSsr from "./pages/CaseStudy/CaseStudySlug";
import CareersPage from "./pages/Careers/Careers";
import EventHome from "./pages/Events/EventHome";
import EventPageSsr from "./pages/Events/EventPage";
import FeedPage from "./pages/FeedPage";
import HomePage from "./pages/Home/HomePage";
import NewsPage from "./pages/NewsPage";
import PageNotFound from "./pages/PageNotFound";
import Partners from "./pages/Partners/Partners";
import Platform from "./pages/Platform";
import OctantAppletsPage from "./pages/Octant/OctantAppletsPage";
import PrivacySsr from "./pages/Privacy";
import IntelligentAlerting from "./pages/Solutions/IntelligentAlerting";
import SolutionsPage from "./pages/Solutions/SolutionsPage";
import IntelligentLogStream from "./pages/Solutions/IntelligentLogStream";
import IntelligentTraceSampling from "./pages/Solutions/IntelligentTraceSampling";
import PiiRedaction from "./pages/Solutions/PiiRedaction";
import TermsSsr from "./pages/Terms";
import { useManageDocumentMetadata } from "./useManageDocumentMetadata";
import { Suspense, lazy, useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { adminTheme } from "./theme/adminTheme";

// Admin routes — only registered when VITE_ADMIN_ENABLED=true (FR-001: production exclusion).
// Vite replaces import.meta.env.VITE_ADMIN_ENABLED at build time; when false/absent the
// lazy() callbacks are never evaluated and admin code is excluded from the production bundle.
const adminEnabled = import.meta.env.VITE_ADMIN_ENABLED === "true";
const adminApiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? "";
const AdminAuthGuard = adminEnabled
  ? lazy(() => import("./pages/Admin/AdminAuthGuard"))
  : null;
const AdminErrorPage = adminEnabled
  ? lazy(() => import("./pages/Admin/AdminErrorPage"))
  : null;
const AdminPage = adminEnabled
  ? lazy(() => import("./pages/Admin/AdminPage"))
  : null;
const OctantProductPage = lazy(() => import("./pages/Octant/OctantProductPage"));
const BlogPost = import.meta.env.SSR
  ? BlogPostSsr
  : lazy(() => import("./pages/Blog/BlogPost"));
const EventPage = import.meta.env.SSR
  ? EventPageSsr
  : lazy(() => import("./pages/Events/EventPage"));
const CaseStudySlug = import.meta.env.SSR
  ? CaseStudySlugSsr
  : lazy(() => import("./pages/CaseStudy/CaseStudySlug"));
const Privacy = import.meta.env.SSR
  ? PrivacySsr
  : lazy(() => import("./pages/Privacy"));
const Terms = import.meta.env.SSR
  ? TermsSsr
  : lazy(() => import("./pages/Terms"));
const ContactModal = lazy(() =>
  import("./components/ContactModal").then((module) => ({
    default: module.ContactModal,
  })),
);
const ScheduleCallModal = lazy(() =>
  import("./components/ScheduleCallModal").then((module) => ({
    default: module.ScheduleCallModal,
  })),
);

const whatsNewRoute = new RegExp(
  /^[/](?<tab>blog|news|case-study|events)([/](?<slug>[a-z0-9-_]+))?$/,
);
const adminRootRoute = /^\/admin\/?$/;

function useHashModalLocation(hash: string) {
  const [urlHash, setUrlHash] = useHashLocation();
  const [open, setOpen] = useState(urlHash === hash);

  const onClose = () => {
    setUrlHash("");
    setOpen(false);
  };

  useEffect(() => {
    if (hash === urlHash) {
      setOpen(true);
    }
  }, [hash, urlHash]);

  return { open, onClose };
}


function App() {
  const [location] = useLocation();

  // Wouter's hashLocation hook replaces the "#" with a "/" for some reason
  const { open: modalOpen, onClose } = useHashModalLocation("/contact-us");
  const { open: callModalOpen, onClose: onCallModalClose } = useHashModalLocation("/schedule-call");

  useManageDocumentMetadata(location);

  const isAuthErrorRoute = location.startsWith(ROUTE.ADMIN_AUTH_ERROR);
  const shouldEnforceSiteAuth =
    adminEnabled &&
    AdminAuthGuard !== null &&
    !isAuthErrorRoute &&
    !import.meta.env.DEV;

  const appShell = (
    <Layout>
      <Switch>
        <Route path={ROUTE.HOME} component={HomePage} />
        <Route path={ROUTE.ABOUT} component={About} />
        <Route path={ROUTE.BLOG_POST}>
          <Suspense fallback={null}>
            <BlogPost />
          </Suspense>
        </Route>
        <Route path={ROUTE.EVENT}>
          <Suspense fallback={null}>
            <EventPage />
          </Suspense>
        </Route>
        <Route path={ROUTE.CASE_STUDY_SLUG}>
          <Suspense fallback={null}>
            <CaseStudySlug />
          </Suspense>
        </Route>
        <Route path={whatsNewRoute}>
          <TabsTilesWrapper>
            <Switch>
              <Route path={ROUTE.BLOG} component={BlogPage} />
              <Route path={ROUTE.EVENTS} component={EventHome} />
              <Route path={ROUTE.NEWS} component={NewsPage} />
              <Route path={ROUTE.CASE_STUDY} component={CaseStudyPage} />
            </Switch>
          </TabsTilesWrapper>
        </Route>

        <Route path={ROUTE.PLATFORM} component={Platform} />
        <Route path={ROUTE.ALL_SOLUTIONS} component={SolutionsPage} />
        <Route
          path={ROUTE.INTELLIGENT_LOGSTREAM}
          component={IntelligentLogStream}
        />
        <Route
          path={ROUTE.INTELLIGENT_TRACE_SAMPLING}
          component={IntelligentTraceSampling}
        />
        <Route
          path={ROUTE.INTELLIGENT_ALERTING}
          component={IntelligentAlerting}
        />
        <Route path={ROUTE.PII_REDACTION} component={PiiRedaction} />
        <Route path={ROUTE.CAREERS} component={CareersPage} />
        <Route path={ROUTE.PARTNERS} component={Partners} />
        <Route path={ROUTE.OCTANT_PARENT} component={OctantAppletsPage} />
        {STATIC_REDIRECTS.map((redirect) => (
          <Route key={redirect.from} path={redirect.from}>
            <Redirect to={redirect.to} />
          </Route>
        ))}
        <Route path={ROUTE.OCTANT_PRODUCT}>
          <Suspense fallback={null}><OctantProductPage /></Suspense>
        </Route>
        <Route path={"/feed"} component={FeedPage} />
        <Route path={ROUTE.PRIVACY}>
          <Suspense fallback={null}>
            <Privacy />
          </Suspense>
        </Route>
        <Route path={ROUTE.TERMS}>
          <Suspense fallback={null}>
            <Terms />
          </Suspense>
        </Route>
        {adminEnabled && AdminPage !== null && (
          <Route path={ROUTE.ADMIN_CONTENT_EDITOR}>
            <ThemeProvider theme={adminTheme}>
              <Suspense fallback={<CircularProgress aria-label="Loading admin portal" sx={{ position: "fixed", top: "50%", left: "50%", translate: "-50% -50%" }} />}>
                <AdminPage apiBaseUrl={adminApiBaseUrl} />
              </Suspense>
            </ThemeProvider>
          </Route>
        )}
        {adminEnabled && AdminErrorPage !== null && (
          <Route path={ROUTE.ADMIN_AUTH_ERROR}>
            <ThemeProvider theme={adminTheme}>
              <Suspense fallback={<CircularProgress aria-label="Loading admin portal" sx={{ position: "fixed", top: "50%", left: "50%", translate: "-50% -50%" }} />}>
                <AdminErrorPage />
              </Suspense>
            </ThemeProvider>
          </Route>
        )}
        {adminEnabled && (
          <Route path={adminRootRoute}>
            <Redirect to={ROUTE.ADMIN_CONTENT_EDITOR} />
          </Route>
        )}
        <PageNotFound />
      </Switch>
    </Layout>
  );

  return (
    <>
      {modalOpen && (
        <Suspense fallback={null}>
          <ContactModal onClose={onClose} open={modalOpen} />
        </Suspense>
      )}
      {callModalOpen && (
        <Suspense fallback={null}>
          <ScheduleCallModal onClose={onCallModalClose} open={callModalOpen} />
        </Suspense>
      )}
      {shouldEnforceSiteAuth ? (
        <Suspense fallback={<CircularProgress aria-label="Checking authentication status" sx={{ position: "fixed", top: "50%", left: "50%", translate: "-50% -50%" }} />}>
          <AdminAuthGuard apiBaseUrl={adminApiBaseUrl}>{appShell}</AdminAuthGuard>
        </Suspense>
      ) : appShell}
    </>
  );
}

export default App;
